import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Share2, ExternalLink, Droplets, Stethoscope, Users, DollarSign } from "lucide-react";
import { ProductGrid } from "../product";
import { SEO } from "../SEO";

const ADORE_VIDEO = "https://customer-assets.emergentagent.com/job_57f3e4d8-87fc-4053-ba36-5d4ff7f3cc21/artifacts/ulcq17ne_adore_rae_final_ad.mp4";
const YOUTH_HOODIE_IMG = "https://customer-assets.emergentagent.com/job_57f3e4d8-87fc-4053-ba36-5d4ff7f3cc21/artifacts/o6aib4hi_hoodie-mockup-featuring-an-ai-created-girl-with-headphones-smiling-in-a-studio-m56050.png";
const BLACK_FRONT = "https://customer-assets.emergentagent.com/job_57f3e4d8-87fc-4053-ba36-5d4ff7f3cc21/artifacts/tyfoonym_black%20hoodie%20front.png";
const BLACK_BACK = "https://customer-assets.emergentagent.com/job_57f3e4d8-87fc-4053-ba36-5d4ff7f3cc21/artifacts/1dod3p3t_black%20hoodie%20back.png";
const WHITE_FRONT = "https://customer-assets.emergentagent.com/job_57f3e4d8-87fc-4053-ba36-5d4ff7f3cc21/artifacts/313w4na5_white%20hoodie%20front.png";
const WHITE_BACK = "https://customer-assets.emergentagent.com/job_57f3e4d8-87fc-4053-ba36-5d4ff7f3cc21/artifacts/teygy3z8_white%20hoodie%20bac.png";

const SICKLE_CELL_FACTS = [
  { icon: Droplets, stat: "100,000+", label: "Americans affected by Sickle Cell Disease" },
  { icon: Stethoscope, stat: "Every 1 in 365", label: "Black children born with Sickle Cell" },
  { icon: Users, stat: "3 Million+", label: "People carry the Sickle Cell trait in the US" },
];

const FUND_BREAKDOWN = [
  { pct: "40%", label: "Medical Bills & Treatment", color: "from-red-500 to-red-600" },
  { pct: "30%", label: "Hospital Stays & Travel", color: "from-pink-500 to-pink-600" },
  { pct: "20%", label: "Daily Care & Medication", color: "from-purple-500 to-purple-600" },
  { pct: "10%", label: "Awareness & Community", color: "from-amber-500 to-amber-600" },
];

function ShareButtons() {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";
  const text = "Support Raegan's fight against Sickle Cell Disease. Every purchase is a donation. Even in the Dark She Still Shines.";

  const copy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-center gap-3 flex-wrap justify-center" data-testid="share-buttons">
      <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer"
        className="px-4 py-2 bg-blue-600/20 border border-blue-500/30 text-blue-300 rounded-lg text-sm font-semibold hover:bg-blue-600/30 transition-all">
        Facebook
      </a>
      <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`} target="_blank" rel="noopener noreferrer"
        className="px-4 py-2 bg-sky-600/20 border border-sky-500/30 text-sky-300 rounded-lg text-sm font-semibold hover:bg-sky-600/30 transition-all">
        Twitter / X
      </a>
      <button onClick={copy}
        className="px-4 py-2 bg-white/10 border border-white/20 text-white/80 rounded-lg text-sm font-semibold hover:bg-white/20 transition-all">
        {copied ? "Copied!" : "Copy Link"}
      </button>
    </div>
  );
}

export function AdoreSection({ products, isPage }) {
  const navigate = useNavigate();
  const adoreProducts = products.filter(
    p => p.collection === "Adore" || (p.name || "").toLowerCase().includes("adore")
  );

  // Non-page mode: compact view for homepage etc.
  if (!isPage) {
    return (
      <section className="py-20 px-4 bg-gradient-to-b from-black via-red-950/10 to-black" data-testid="adore-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-red-300 text-xs tracking-widest uppercase font-bold">Sickle Cell Awareness</span>
            <h2 className="text-white text-5xl sm:text-6xl font-bebas mt-2">
              Adore Rae <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 via-pink-400 to-purple-400">Collection</span>
            </h2>
          </div>
          <ProductGrid products={adoreProducts} />
          {adoreProducts.length > 0 && (
            <div className="text-center mt-12">
              <button onClick={() => navigate("/adore")}
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-red-600 to-red-500 text-white font-semibold hover:from-red-500 hover:to-red-400 transition-all rounded-lg"
                data-testid="view-adore-collection-btn">
                <Heart size={16} fill="currentColor" /> View Adore Rae Collection
              </button>
            </div>
          )}
        </div>
      </section>
    );
  }

  // Full page campaign layout
  return (
    <div className="bg-black min-h-screen" data-testid="adore-section">

      <SEO 
        title="Adore Rae - Sickle Cell Awareness Collection"
        description="Adore Rae Collection by Fresh Po'Boy. 100% of proceeds support Raegan's fight against Sickle Cell Disease. Hoodies, Tees & more. Even in the Dark She Still Shines."
        path="/adore"
      />

      {/* Sticky Donate Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-r from-red-900/95 via-red-800/95 to-red-900/95 backdrop-blur-md border-t border-red-500/30 py-3 px-4" data-testid="sticky-donate-bar">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <p className="text-white/80 text-sm hidden sm:block">
            <Heart size={14} className="inline text-red-400 mr-1" fill="currentColor" />
            100% of proceeds support Raegan's fight
          </p>
          <div className="flex items-center gap-3 mx-auto sm:mx-0">
            <button onClick={() => navigate("/shay-adore")}
              className="px-6 py-2.5 bg-white text-red-700 font-bold rounded-full hover:bg-red-50 transition-all text-sm shadow-lg"
              data-testid="sticky-donate-btn">
              Donate Now
            </button>
            <a href="https://www.adorerae.com" target="_blank" rel="noopener noreferrer"
              className="px-4 py-2.5 border border-white/30 text-white font-semibold rounded-full hover:bg-white/10 transition-all text-sm">
              adorerae.com
            </a>
          </div>
        </div>
      </div>

      {/* Hero Section - Full Width Video */}
      <section className="relative pt-20">
        <div className="relative w-full max-w-6xl mx-auto px-4">
          <div className="relative rounded-3xl overflow-hidden border border-red-500/20 shadow-2xl shadow-red-900/30">
            <video src={ADORE_VIDEO} className="w-full aspect-video object-cover" autoPlay muted loop playsInline data-testid="adore-hero-video" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent flex items-end">
              <div className="p-8 sm:p-12 w-full">
                <div className="inline-flex items-center gap-2 bg-red-500/30 backdrop-blur-sm border border-red-400/40 rounded-full px-5 py-2 mb-4">
                  <Heart size={14} className="text-red-300" fill="currentColor" />
                  <span className="text-red-200 text-xs tracking-widest uppercase font-bold">Sickle Cell & Liver Disease Awareness</span>
                </div>
                <h1 className="text-white text-4xl sm:text-6xl lg:text-7xl font-bebas leading-tight">
                  Even in the Dark<br />She Still <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 via-pink-400 to-amber-400">Shines</span>
                </h1>
                <p className="text-white/70 text-base sm:text-lg mt-4 max-w-xl">
                  This is more than a brand. It's a movement for <strong className="text-white">Raegan (Rae) Robinson</strong> — a warrior fighting Sickle Cell Disease every single day.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rae's Story Timeline */}
      <section className="py-20 px-4" data-testid="rae-story-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-pink-400 text-xs tracking-widest uppercase font-bold">Her Journey</span>
            <h2 className="text-white text-4xl sm:text-5xl font-bebas mt-2">Rae's Story</h2>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-6 sm:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-red-500 via-pink-500 to-purple-500" />

            {/* Timeline events */}
            <div className="space-y-12">
              <div className="relative flex flex-col sm:flex-row items-start gap-6 sm:gap-12">
                <div className="sm:w-1/2 sm:text-right sm:pr-12">
                  <div className="bg-zinc-900/80 rounded-2xl p-6 border border-red-500/20 ml-14 sm:ml-0">
                    <span className="text-red-400 text-xs font-bold tracking-wider">THE BEGINNING</span>
                    <h3 className="text-white text-xl font-bold mt-1">Raegan's Diagnosis</h3>
                    <p className="text-white/60 text-sm mt-2">Raegan was diagnosed with Sickle Cell Anemia, a genetic blood disorder that causes red blood cells to become misshapen, leading to pain crises and organ damage.</p>
                  </div>
                </div>
                <div className="absolute left-3.5 sm:left-1/2 sm:-translate-x-1/2 w-5 h-5 bg-red-500 rounded-full border-4 border-black" />
                <div className="sm:w-1/2" />
              </div>

              <div className="relative flex flex-col sm:flex-row items-start gap-6 sm:gap-12">
                <div className="sm:w-1/2" />
                <div className="absolute left-3.5 sm:left-1/2 sm:-translate-x-1/2 w-5 h-5 bg-pink-500 rounded-full border-4 border-black" />
                <div className="sm:w-1/2 sm:pl-12">
                  <div className="bg-zinc-900/80 rounded-2xl p-6 border border-pink-500/20 ml-14 sm:ml-0">
                    <span className="text-pink-400 text-xs font-bold tracking-wider">THE FIGHT</span>
                    <h3 className="text-white text-xl font-bold mt-1">Liver Disease Complications</h3>
                    <p className="text-white/60 text-sm mt-2">The disease progressed to serious liver complications, requiring frequent hospital visits, blood transfusions, and constant medical care.</p>
                  </div>
                </div>
              </div>

              <div className="relative flex flex-col sm:flex-row items-start gap-6 sm:gap-12">
                <div className="sm:w-1/2 sm:text-right sm:pr-12">
                  <div className="bg-zinc-900/80 rounded-2xl p-6 border border-purple-500/20 ml-14 sm:ml-0">
                    <span className="text-purple-400 text-xs font-bold tracking-wider">THE LIGHT</span>
                    <h3 className="text-white text-xl font-bold mt-1">Adore Rae Is Born</h3>
                    <p className="text-white/60 text-sm mt-2">Her mother Shaynise (Shay) Robinson created Adore Rae — a brand born from love, raising awareness and funds for Rae's ongoing treatment. Every piece tells Rae's story.</p>
                  </div>
                </div>
                <div className="absolute left-3.5 sm:left-1/2 sm:-translate-x-1/2 w-5 h-5 bg-purple-500 rounded-full border-4 border-black" />
                <div className="sm:w-1/2" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Sickle Cell Matters - Infographic */}
      <section className="py-20 px-4 bg-gradient-to-b from-transparent via-red-950/10 to-transparent" data-testid="sickle-cell-facts">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-red-400 text-xs tracking-widest uppercase font-bold">Know The Facts</span>
            <h2 className="text-white text-4xl sm:text-5xl font-bebas mt-2">Why Sickle Cell <span className="text-red-400">Matters</span></h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {SICKLE_CELL_FACTS.map((fact) => (
              <div key={fact.label} className="bg-zinc-900/60 backdrop-blur rounded-2xl p-8 border border-red-500/10 text-center hover:border-red-500/30 transition-all">
                <fact.icon size={32} className="text-red-400 mx-auto mb-4" />
                <p className="text-white text-3xl font-bebas">{fact.stat}</p>
                <p className="text-white/50 text-sm mt-2">{fact.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Youth Hoodie Callout */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="rounded-3xl overflow-hidden border border-red-500/20 shadow-xl">
              <img src={YOUTH_HOODIE_IMG} alt="Adore Rae The Light Youth Hoodie" className="w-full object-cover" />
            </div>
            <div className="lg:pl-8">
              <span className="text-pink-400 text-xs tracking-widest uppercase font-bold">Featured</span>
              <h2 className="text-white text-4xl sm:text-5xl font-bebas mt-2">Rae. The <span className="text-amber-400">Light!</span></h2>
              <p className="text-white/60 mt-4 leading-relaxed">
                "Even in the Dark She Still Shines" — This hoodie carries Raegan's spirit. Available in youth and adult sizes, every purchase goes directly toward her medical care.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button onClick={() => navigate("/shay-adore")}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-500 hover:to-pink-500 text-white font-bold rounded-xl transition-all shadow-lg shadow-red-500/25 text-lg"
                  data-testid="adore-donate-cta">
                  <Heart size={18} fill="currentColor" /> Shop & Donate
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hoodie Front & Back Showcase */}
      <section className="py-20 px-4" data-testid="adore-hoodie-showcase">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-white text-4xl sm:text-5xl font-bebas">Hoodie <span className="text-red-400">Front & Back</span></h2>
          </div>
          <div className="space-y-10">
            <div className="bg-zinc-900/50 rounded-2xl border border-white/10 overflow-hidden p-4 sm:p-6">
              <p className="text-white font-semibold text-lg mb-4 text-center">Adore Rae — <span className="text-red-400">Black Hoodie</span></p>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-xl overflow-hidden bg-zinc-800">
                  <img src={BLACK_FRONT} alt="Adore Rae Black Hoodie Front" className="w-full h-full object-cover" loading="lazy" />
                  <p className="text-white/50 text-xs text-center py-2">Front</p>
                </div>
                <div className="rounded-xl overflow-hidden bg-zinc-800">
                  <img src={BLACK_BACK} alt="Adore Rae Black Hoodie Back" className="w-full h-full object-cover" loading="lazy" />
                  <p className="text-white/50 text-xs text-center py-2">Back</p>
                </div>
              </div>
            </div>
            <div className="bg-zinc-900/50 rounded-2xl border border-white/10 overflow-hidden p-4 sm:p-6">
              <p className="text-white font-semibold text-lg mb-4 text-center">Adore Rae — <span className="text-red-400">White Hoodie</span></p>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-xl overflow-hidden bg-zinc-800">
                  <img src={WHITE_FRONT} alt="Adore Rae White Hoodie Front" className="w-full h-full object-cover" loading="lazy" />
                  <p className="text-white/50 text-xs text-center py-2">Front</p>
                </div>
                <div className="rounded-xl overflow-hidden bg-zinc-800">
                  <img src={WHITE_BACK} alt="Adore Rae White Hoodie Back" className="w-full h-full object-cover" loading="lazy" />
                  <p className="text-white/50 text-xs text-center py-2">Back</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Where Your Money Goes */}
      <section className="py-20 px-4 bg-gradient-to-b from-transparent via-red-950/10 to-transparent" data-testid="fund-breakdown">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-amber-400 text-xs tracking-widest uppercase font-bold">Full Transparency</span>
            <h2 className="text-white text-4xl sm:text-5xl font-bebas mt-2">Where Your <span className="text-amber-400">Money Goes</span></h2>
            <p className="text-white/50 mt-3">Every dollar from Adore Rae products goes directly to supporting Raegan.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {FUND_BREAKDOWN.map((item) => (
              <div key={item.label} className="bg-zinc-900/60 rounded-2xl p-6 border border-white/5 flex items-center gap-5">
                <div className={`bg-gradient-to-br ${item.color} w-16 h-16 rounded-xl flex items-center justify-center shrink-0`}>
                  <span className="text-white font-bebas text-2xl">{item.pct}</span>
                </div>
                <div>
                  <p className="text-white font-semibold">{item.label}</p>
                  <DollarSign size={14} className="text-white/30 mt-1 inline" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Grid */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-red-300 text-xs tracking-widest uppercase font-bold">Wear Your Heart</span>
            <h2 className="text-white text-4xl sm:text-5xl font-bebas mt-2">
              Adore Rae <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 via-pink-400 to-purple-400">Collection</span>
            </h2>
          </div>
          <ProductGrid products={adoreProducts} />
          {adoreProducts.length === 0 && (
            <div className="text-center py-16">
              <div className="bg-zinc-900/50 rounded-2xl p-12 max-w-2xl mx-auto border border-red-500/20">
                <Heart size={48} className="text-red-400 mx-auto mb-4" />
                <h3 className="text-white text-2xl font-bebas mb-4">Collection Coming Soon</h3>
                <p className="text-white/60 mb-6">New Adore Rae awareness designs are on the way.</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Share Section */}
      <section className="py-16 px-4" data-testid="share-section">
        <div className="max-w-2xl mx-auto text-center">
          <Share2 size={28} className="text-pink-400 mx-auto mb-4" />
          <h2 className="text-white text-3xl sm:text-4xl font-bebas">Share Rae's Story</h2>
          <p className="text-white/50 mt-3 mb-8 text-sm">Help spread awareness. Every share brings Raegan closer to the care she needs.</p>
          <ShareButtons />
          <a href="https://www.adorerae.com" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-6 text-pink-400 hover:text-pink-300 transition-colors text-sm font-semibold"
            data-testid="adore-journey-link">
            <ExternalLink size={14} /> Follow Rae's full journey at adorerae.com
          </a>
        </div>
      </section>

      {/* Bottom spacer for sticky bar */}
      <div className="h-16" />
    </div>
  );
}
